/**
 * Historical Data Learning Service
 * 
 * Analyzes historical transactions and generates rules based on patterns.
 * Uses frequency, consistency, and recency to calculate confidence scores.
 */

import Papa from 'papaparse';
import { supabase } from '@/integrations/supabase/client';

export interface HistoricalTransaction {
  date?: string;
  counterparty?: string;
  description?: string;
  amount?: number;
  account?: string;
  accountNumber?: string;
  vatRate?: number;
}

export interface PatternRule {
  pattern_type: 'counterparty' | 'description' | 'amount_range';
  pattern_value: string;
  account: string;
  vat_rate: number;
  confidence: number;
  frequency: number;
  consistency: number;
  reasoning: string;
  sample_transactions: string[];
}

export interface LearningAnalysis {
  totalTransactions: number;
  patternsFound: number;
  rulesGenerated: PatternRule[];
  lowConfidencePatterns: PatternRule[];
  processingTimeMs: number;
  aiEnhanced?: boolean;
  mergedPatterns?: number;
  enhancementTimeMs?: number;
}

/**
 * Parse CSV file with historical transactions
 */
export const parseHistoricalCSV = async (file: File): Promise<HistoricalTransaction[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const transactions = results.data.map((row: any) => {
          // Flexible column mapping - supports various formats
          const counterparty = row['Counterparty'] || row['counterparty'] || row['Gegenpartei'] || row['Partner'] || '';
          const description = row['Description'] || row['description'] || row['Beschreibung'] || row['Text'] || '';
          const account = row['Account'] || row['account'] || row['Konto'] || row['Account Number'] || '';
          const amount = parseFloat(row['Amount'] || row['amount'] || row['Betrag'] || row['Value'] || '0');
          const vatRate = parseFloat(row['VAT'] || row['vat'] || row['MWST'] || row['VAT Rate'] || '8.1');
          const date = row['Date'] || row['date'] || row['Datum'] || '';

          return {
            date,
            counterparty: counterparty.trim(),
            description: description.trim(),
            amount,
            account: account.trim(),
            accountNumber: account.split(' ')[0]?.trim() || account.trim(),
            vatRate: isNaN(vatRate) ? 8.1 : vatRate,
          };
        });

        // Filter out invalid rows
        const validTransactions = transactions.filter(
          (tx: HistoricalTransaction) => tx.counterparty || tx.description
        );

        resolve(validTransactions);
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      },
    });
  });
};

/**
 * Analyze patterns from historical transactions
 */
export const analyzePatterns = (transactions: HistoricalTransaction[]): PatternRule[] => {
  // Group by counterparty
  const counterpartyMap = new Map<string, HistoricalTransaction[]>();
  transactions.forEach(tx => {
    if (tx.counterparty && tx.accountNumber) {
      const key = tx.counterparty.toLowerCase();
      if (!counterpartyMap.has(key)) {
        counterpartyMap.set(key, []);
      }
      counterpartyMap.get(key)!.push(tx);
    }
  });

  const rules: PatternRule[] = [];

  // Generate counterparty-based rules
  counterpartyMap.forEach((txs, counterparty) => {
    if (txs.length < 2) return; // Minimum 2 occurrences

    // Calculate account consistency
    const accountCounts = new Map<string, number>();
    txs.forEach(tx => {
      const acc = tx.accountNumber!;
      accountCounts.set(acc, (accountCounts.get(acc) || 0) + 1);
    });

    // Get most frequent account
    const sortedAccounts = Array.from(accountCounts.entries()).sort((a, b) => b[1] - a[1]);
    const [mostFrequentAccount, count] = sortedAccounts[0];
    
    const frequency = txs.length;
    const consistency = count / txs.length;

    // Calculate confidence (0-100%)
    // Formula: (frequency * 10 + consistency * 90) capped at 100
    const frequencyScore = Math.min(frequency * 5, 50); // Max 50 points from frequency
    const consistencyScore = consistency * 50; // Max 50 points from consistency
    const confidence = Math.min(Math.round(frequencyScore + consistencyScore), 100);

    // Only include if confidence >= 60%
    if (confidence >= 60) {
      // Get average VAT rate
      const avgVatRate = txs.reduce((sum, tx) => sum + (tx.vatRate || 8.1), 0) / txs.length;

      rules.push({
        pattern_type: 'counterparty',
        pattern_value: counterparty,
        account: mostFrequentAccount,
        vat_rate: Math.round(avgVatRate * 10) / 10,
        confidence,
        frequency,
        consistency: Math.round(consistency * 100),
        reasoning: `Pattern found in ${frequency} transactions with ${Math.round(consistency * 100)}% consistency`,
        sample_transactions: txs.slice(0, 3).map(tx => 
          `${tx.date || 'N/A'}: ${tx.description || tx.counterparty} - CHF ${tx.amount?.toFixed(2) || '0.00'}`
        ),
      });
    }
  });

  // Group by description keywords (top 3 word combinations)
  const descriptionMap = new Map<string, HistoricalTransaction[]>();
  transactions.forEach(tx => {
    if (tx.description && tx.accountNumber && !tx.counterparty) {
      // Extract significant keywords (3+ characters, not numbers)
      const keywords = tx.description
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length >= 3 && !/^\d+$/.test(word))
        .slice(0, 3)
        .join(' ');
      
      if (keywords) {
        if (!descriptionMap.has(keywords)) {
          descriptionMap.set(keywords, []);
        }
        descriptionMap.get(keywords)!.push(tx);
      }
    }
  });

  // Generate description-based rules
  descriptionMap.forEach((txs, keywords) => {
    if (txs.length < 3) return; // Minimum 3 occurrences for description patterns

    const accountCounts = new Map<string, number>();
    txs.forEach(tx => {
      const acc = tx.accountNumber!;
      accountCounts.set(acc, (accountCounts.get(acc) || 0) + 1);
    });

    const sortedAccounts = Array.from(accountCounts.entries()).sort((a, b) => b[1] - a[1]);
    const [mostFrequentAccount, count] = sortedAccounts[0];
    
    const frequency = txs.length;
    const consistency = count / txs.length;

    const frequencyScore = Math.min(frequency * 5, 50);
    const consistencyScore = consistency * 50;
    const confidence = Math.min(Math.round(frequencyScore + consistencyScore), 100);

    if (confidence >= 60) {
      const avgVatRate = txs.reduce((sum, tx) => sum + (tx.vatRate || 8.1), 0) / txs.length;

      rules.push({
        pattern_type: 'description',
        pattern_value: keywords,
        account: mostFrequentAccount,
        vat_rate: Math.round(avgVatRate * 10) / 10,
        confidence,
        frequency,
        consistency: Math.round(consistency * 100),
        reasoning: `Description pattern found in ${frequency} transactions with ${Math.round(consistency * 100)}% consistency`,
        sample_transactions: txs.slice(0, 3).map(tx => 
          `${tx.date || 'N/A'}: ${tx.description} - CHF ${tx.amount?.toFixed(2) || '0.00'}`
        ),
      });
    }
  });

  // Sort by confidence descending
  rules.sort((a, b) => b.confidence - a.confidence);

  return rules;
};

/**
 * Analyze historical data and return learning analysis
 */
export const analyzeHistoricalData = async (file: File): Promise<LearningAnalysis> => {
  const startTime = Date.now();

  // Parse CSV
  const transactions = await parseHistoricalCSV(file);

  if (transactions.length === 0) {
    throw new Error('No valid transactions found in file');
  }

  // Analyze patterns
  const allRules = analyzePatterns(transactions);

  // Split into high/low confidence
  const rulesGenerated = allRules.filter(r => r.confidence >= 75);
  const lowConfidencePatterns = allRules.filter(r => r.confidence >= 60 && r.confidence < 75);

  const processingTimeMs = Date.now() - startTime;

  return {
    totalTransactions: transactions.length,
    patternsFound: allRules.length,
    rulesGenerated,
    lowConfidencePatterns,
    processingTimeMs,
  };
};

/**
 * Save learned rules to database
 */
export const saveLearnedRules = async (
  rules: PatternRule[],
  organizationId: string
): Promise<{ success: number; failed: number }> => {
  let success = 0;
  let failed = 0;

  for (const rule of rules) {
    try {
      const insertData: any = {
        organization_id: organizationId,
        account: rule.account,
        vat_rate: rule.vat_rate,
        confidence: rule.confidence,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (rule.pattern_type === 'counterparty') {
        insertData.counterparty = rule.pattern_value;
        insertData.description_pattern = '';
      } else {
        insertData.counterparty = '';
        insertData.description_pattern = rule.pattern_value;
      }

      const { error } = await supabase
        .from('transaction_patterns')
        .insert(insertData);

      if (error) {
        console.error('Failed to save rule:', error);
        failed++;
      } else {
        success++;
      }
    } catch (error) {
      console.error('Exception saving rule:', error);
      failed++;
    }
  }

  return { success, failed };
};

/**
 * AI Enhancement: Use Claude Haiku to normalize and merge similar patterns
 */
export const enhanceWithAI = async (rules: PatternRule[]): Promise<PatternRule[]> => {
  const startTime = Date.now();

  // Group rules by account to find potential duplicates
  const rulesByAccount = new Map<string, PatternRule[]>();
  rules.forEach(rule => {
    if (!rulesByAccount.has(rule.account)) {
      rulesByAccount.set(rule.account, []);
    }
    rulesByAccount.get(rule.account)!.push(rule);
  });

  const enhancedRules: PatternRule[] = [];

  // Process each account group
  for (const [account, accountRules] of rulesByAccount.entries()) {
    if (accountRules.length === 1) {
      // No duplicates possible, keep as is
      enhancedRules.push(accountRules[0]);
      continue;
    }

    // Call Claude Haiku to analyze and merge similar patterns
    const prompt = `You are a financial data analyst. Analyze these transaction patterns and identify which ones represent the same entity/pattern that should be merged.

Patterns for account ${account}:
${accountRules.map((r, i) => `${i + 1}. "${r.pattern_value}" (${r.pattern_type}, ${r.frequency}x, ${r.confidence}% confidence)`).join('\n')}

TASK:
1. Identify groups of patterns that represent the same entity (e.g., "Stripe Payments" and "STRIPE PAYMENTS UK" are the same)
2. For each group, suggest a normalized pattern name
3. Return JSON format ONLY:

{
  "mergeGroups": [
    {
      "normalizedName": "stripe",
      "patterns": [0, 1, 2],
      "reasoning": "All are Stripe payment processor variants"
    }
  ],
  "keepSeparate": [3, 4]
}

RULES:
- Only merge if you're >90% confident they're the same entity
- Normalized names should be lowercase and generic
- If unsure, keep separate
- Return valid JSON only`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_ANTHROPIC_API_KEY || '',
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: 'claude-3-haiku-20240307',
          max_tokens: 1024,
          messages: [{
            role: 'user',
            content: prompt,
          }],
        }),
      });

      if (!response.ok) {
        console.error('AI enhancement failed, keeping original rules');
        enhancedRules.push(...accountRules);
        continue;
      }

      const data = await response.json();
      const content = data.content[0].text;
      
      // Extract JSON from response (might be wrapped in markdown)
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        enhancedRules.push(...accountRules);
        continue;
      }

      const analysis = JSON.parse(jsonMatch[0]);

      // Process merge groups
      if (analysis.mergeGroups && analysis.mergeGroups.length > 0) {
        for (const group of analysis.mergeGroups) {
          const patternsToMerge = group.patterns.map((idx: number) => accountRules[idx]);
          
          // Merge rules
          const mergedRule: PatternRule = {
            pattern_type: patternsToMerge[0].pattern_type,
            pattern_value: group.normalizedName,
            account: account,
            vat_rate: patternsToMerge[0].vat_rate,
            confidence: Math.min(
              100,
              Math.round(
                patternsToMerge.reduce((sum, r) => sum + r.confidence, 0) / patternsToMerge.length + 10
              )
            ), // Boost confidence for merged rules
            frequency: patternsToMerge.reduce((sum, r) => sum + r.frequency, 0),
            consistency: Math.round(
              patternsToMerge.reduce((sum, r) => sum + r.consistency, 0) / patternsToMerge.length
            ),
            reasoning: `Merged ${patternsToMerge.length} patterns: ${group.reasoning}`,
            sample_transactions: patternsToMerge.flatMap(r => r.sample_transactions).slice(0, 3),
          };

          enhancedRules.push(mergedRule);
        }
      }

      // Keep separate rules
      if (analysis.keepSeparate && analysis.keepSeparate.length > 0) {
        for (const idx of analysis.keepSeparate) {
          enhancedRules.push(accountRules[idx]);
        }
      }

    } catch (error) {
      console.error('AI enhancement error:', error);
      enhancedRules.push(...accountRules);
    }
  }

  // Sort by confidence descending
  enhancedRules.sort((a, b) => b.confidence - a.confidence);

  console.log(`AI Enhancement completed in ${Date.now() - startTime}ms`);
  return enhancedRules;
};
